import React, { useState, useEffect } from 'react';
import { TrendingUp, Activity, Database, Zap } from 'lucide-react';
import { Editorial, Typography, BorderRadius } from '../styles/primitives';

interface MarketLoadingScreenProps {
  onComplete?: () => void;
}

const LOADING_MESSAGES = [
  { text: 'Connecting to market feeds...', icon: Activity },
  { text: 'Loading real-time prices...', icon: TrendingUp },
  { text: 'Preparing trading pairs...', icon: Database },
  { text: 'Synchronizing wallet data...', icon: Zap },
  { text: 'Finalizing setup...', icon: Activity }
];

const MarketLoadingScreen: React.FC<MarketLoadingScreenProps> = ({ onComplete }) => {
  const [messageIndex, setMessageIndex] = useState(0);
  const [progress, setProgress] = useState(0);

  useEffect(() => {
    const messageInterval = setInterval(() => {
      setMessageIndex((prev) => {
        if (prev < LOADING_MESSAGES.length - 1) {
          return prev + 1;
        }
        return prev;
      });
    }, 400);

    const progressInterval = setInterval(() => {
      setProgress((prev) => {
        if (prev >= 100) {
          clearInterval(progressInterval);
          return 100;
        }
        return prev + 2;
      });
    }, 40);

    const completeTimer = setTimeout(() => {
      if (onComplete) {
        onComplete();
      }
    }, 2200);

    return () => {
      clearInterval(messageInterval);
      clearInterval(progressInterval);
      clearTimeout(completeTimer);
    };
  }, []);

  useEffect(() => {
    if (progress === 100) {
      const delayTimer = setTimeout(() => {
        if (onComplete) {
          onComplete();
        }
      }, 300);
      return () => clearTimeout(delayTimer);
    }
  }, [progress, onComplete]);

  return (
    <div
      className="fixed inset-0 flex items-center justify-center z-50"
      style={{ backgroundColor: Editorial.Paper }}
    >
      <div className="text-center relative z-10">
        <div className="relative mb-8 flex justify-center items-end gap-2 h-24">
          <div
            className="w-3 animate-pulse"
            style={{
              height: '40%',
              backgroundColor: '#ef4444',
              animationDuration: '1.2s',
              animationDelay: '0s',
            }}
          />
          <div
            className="w-3 animate-pulse"
            style={{
              height: '70%',
              backgroundColor: '#000000',
              animationDuration: '1.2s',
              animationDelay: '0.2s',
            }}
          />
          <div
            className="w-3 animate-pulse"
            style={{
              height: '90%',
              backgroundColor: '#ffffff',
              border: '1px solid #000000',
              animationDuration: '1.2s',
              animationDelay: '0.4s',
            }}
          />
          <div
            className="w-3 animate-pulse"
            style={{
              height: '55%',
              backgroundColor: '#ef4444',
              animationDuration: '1.2s',
              animationDelay: '0.6s',
            }}
          />
          <div
            className="w-3 animate-pulse"
            style={{
              height: '80%',
              backgroundColor: '#000000',
              animationDuration: '1.2s',
              animationDelay: '0.8s',
            }}
          />
          <div
            className="w-3 animate-pulse"
            style={{
              height: '45%',
              backgroundColor: '#ffffff',
              border: '1px solid #000000',
              animationDuration: '1.2s',
              animationDelay: '1s',
            }}
          />
          <div
            className="w-3 animate-pulse"
            style={{
              height: '65%',
              backgroundColor: '#ef4444',
              animationDuration: '1.2s',
              animationDelay: '1.2s',
            }}
          />
        </div>

        <div className="space-y-4 min-h-[120px]">
          <h2
            className="font-semibold"
            style={{
              color: Editorial.Ink,
              fontSize: Typography.HeadlineLarge,
              fontWeight: Typography.WeightSemibold,
              lineHeight: Typography.LeadingTight,
            }}
          >
            Getting Markets Ready
          </h2>

          <div className="h-8 flex items-center justify-center">
            <p
              className="transition-all duration-300"
              style={{
                color: Editorial.InkMedium,
                fontSize: Typography.BodyMedium,
                lineHeight: Typography.LeadingNormal,
              }}
            >
              {LOADING_MESSAGES[messageIndex].text}
            </p>
          </div>

          <div className="w-80 mx-auto mt-6">
            <div
              className="h-2 overflow-hidden border"
              style={{
                backgroundColor: '#ffffff',
                borderColor: '#000000',
                borderRadius: BorderRadius.None,
              }}
            >
              <div
                className="h-full transition-all duration-300 ease-out"
                style={{
                  width: `${progress}%`,
                  background: 'linear-gradient(90deg, #ef4444 0%, #000000 50%, #ef4444 100%)',
                  borderRadius: BorderRadius.None,
                }}
              />
            </div>
            <p
              className="mt-2"
              style={{
                color: Editorial.InkLight,
                fontSize: Typography.BodySmall,
                lineHeight: Typography.LeadingNormal,
              }}
            >
              {Math.round(progress)}% Complete
            </p>
          </div>
        </div>

        <div
          className="mt-8 flex items-center justify-center gap-2"
          style={{
            color: Editorial.InkLight,
            fontSize: Typography.BodySmall,
          }}
        >
          <div className="flex gap-1">
            <div
              className="w-2 h-2 animate-pulse"
              style={{
                backgroundColor: '#ef4444',
                borderRadius: '50%',
                animationDelay: '0s',
              }}
            />
            <div
              className="w-2 h-2 animate-pulse"
              style={{
                backgroundColor: '#000000',
                borderRadius: '50%',
                animationDelay: '0.3s',
              }}
            />
            <div
              className="w-2 h-2 animate-pulse"
              style={{
                backgroundColor: '#ffffff',
                border: '1px solid #000000',
                borderRadius: '50%',
                animationDelay: '0.6s',
              }}
            />
          </div>
          <span>Secure connection established</span>
        </div>
      </div>
    </div>
  );
};

export default MarketLoadingScreen;
